import React, { FC, useContext, useEffect, useState } from "react";
import { Grid, Popover, Theme } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { POPUPS, TEXTS, STATE } from "../../constants";
import { DRUG_POPUP } from "../../apollo/drugPopup";
import { useLazyQuery } from "@apollo/client";
import { LoadingContext } from "../../context";
import { normalize } from "../../functions";
import { PipelineDrugIndications } from "../../typescript/types/data";
import StyledPaper from "../Common/StyledPaper";
import ClinicalDataBtn from "../Buttons/ClinicalData";
import Disclaimer from "./Disclaimer";
import Indications from "./Indications";
import Header from "./Header";
import DrugNames from "./DrugNames";

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.secondary.dark,
    paddingTop: "0.6rem",
    paddingBottom: "0.6rem",
    fontSize: "1.1rem",

    display: "flex",
    justifyContent: "center",
  },
  otherDrugs: {
    display: "flex",
    justifyContent: "center",
  },
  body: {
    padding: "0.8rem",
  },
  action: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "12px",
  },
}));

interface PopupProps {
  rowData: any;
  isOpen: boolean;
  anchorEl: any;
  closePopover: () => any;
}

const Popup: FC<PopupProps> = ({ rowData, isOpen, anchorEl, closePopover }) => {
  const classes = useStyles();

  const { drugId, isBiologic, forecastGuid, brandName, otherPipelineNames } = rowData;

  const [loadingContext, setLoadingContext] = useContext(LoadingContext);

  const [indications, setIndications] = useState<PipelineDrugIndications>(STATE.PIPELINE_DRUG_INDICATIONS);
  const [getDrug, { called, loading, data }] = useLazyQuery(DRUG_POPUP, {
    variables: { drugId },
  });

  useEffect(() => {
    if (isOpen && !data) {
      setLoadingContext({
        ...loadingContext,
        drugPopup: true,
      });
      getDrug();
    }
  }, [isOpen]);

  useEffect(() => {
    if (called && !loading && data) {
      setLoadingContext({
        ...loadingContext,
        drugPopup: false,
      });

      const { approvedIndications, clinicalIndications } = normalize.drugPopup(data.drug);

      setIndications({
        ...indications,
        approvedIndications,
        clinicalIndications,
      });
    }
  }, [data]);

  return (
    <Popover
      id={drugId}
      open={isOpen}
      anchorEl={anchorEl}
      onClose={closePopover}
      anchorOrigin={POPUPS.ANCHOR_ORIGIN}
      transformOrigin={POPUPS.TRANSFORM_ORIGIN}
    >
      <StyledPaper elevation={7} width="400px">
        <Grid container>
          <Grid item xs={12} className={classes.header}>
            <Header isBiologic={isBiologic} brandName={brandName} />
          </Grid>
          <Grid item xs={12} className={classes.body}>
            <Grid item xs={12}>
              <DrugNames drugs={otherPipelineNames} />
            </Grid>
            <Grid item xs={12}>
              <Indications sectionTitle="Approved Indications" indications={indications.approvedIndications} />
            </Grid>
            <Grid item xs={12}>
              <Indications sectionTitle="Clinical Indications" indications={indications.clinicalIndications} />
            </Grid>
            <Grid item xs={12}>
              <Disclaimer message={TEXTS.INDICATION_TOOLTIP} />
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.action}>
            <ClinicalDataBtn brandName={brandName} />
          </Grid>
        </Grid>
      </StyledPaper>
    </Popover>
  );
};

export default Popup;
